import { useState, useCallback, useEffect, useRef } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useTattooDetail } from "@/hooks/useTattooDetail"
import { useArtists } from "@/hooks/useArtists"
import { useAnalytics } from "@/hooks/useAnalytics"
import { useAuthState } from "@/hooks/useAuth"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Share2, Send } from "lucide-react"
import { AuthModal } from "@/components/auth/AuthModal"
import { ShareModal } from "@/components/shared/ShareModal"
import { SaveButton } from "@/components/shared/tattoo/SaveButton"
import { SimilarItems } from "@/components/details/SimilarItems"

const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
}

interface TattooDetailContentProps {
  tattooId: string
  onBack?: () => void
  shareUrl?: string
}

interface DetailNavigationState {
  from?: {
    pathname: string
    search?: string
    hash?: string
  }
  scrollPosition?: number
}

export function TattooDetailContent({ tattooId, onBack, shareUrl }: TattooDetailContentProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { tattoo, loading, error } = useTattooDetail(tattooId)
  const { artists } = useArtists(tattoo ? [tattoo.artistId] : [])
  const { trackEvent } = useAnalytics()
  const { user } = useAuthState()
  
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authModalContext, setAuthModalContext] = useState<'contact' | 'share'>('contact')
  const [showShareModal, setShowShareModal] = useState(false)
  const [compressedImageLoaded, setCompressedImageLoaded] = useState(false)
  const [fullImageLoaded, setFullImageLoaded] = useState(false)

  const imageLoaded = compressedImageLoaded || fullImageLoaded
  
  // Pinch-to-zoom and pan state
  const imageContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isZooming, setIsZooming] = useState(false)
  const [isPanning, setIsPanning] = useState(false)
  const initialDistanceRef = useRef<number>(0)
  const initialScaleRef = useRef<number>(1)
  const initialPositionRef = useRef({ x: 0, y: 0 })
  const lastTouchCenterRef = useRef({ x: 0, y: 0 })
  const touchStartTimeRef = useRef<number>(0)

  const artist = artists[0]

  const [detailPageSavesCount, setDetailPageSavesCount] = useState(0)

  const handleDetailSaveCountChange = useCallback((_isSaved: boolean, newSavesCount: number) => {
    setDetailPageSavesCount(newSavesCount)
  }, [])

  useEffect(() => {
    setDetailPageSavesCount(0)
  }, [tattooId])

  // Scroll to top when tattooId changes (e.g., when clicking on similar items)
  useEffect(() => {
    if (!tattoo) return // Wait for content to load
    
    // Use setTimeout to ensure content is rendered and then scroll
    const scrollToTop = () => {
      // Try to find the scrollable parent container
      let scrollableElement: Element | null = null
      
      // Start from our content ref and walk up the tree
      if (contentRef.current) {
        let element = contentRef.current.parentElement
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY
          if (overflowY === 'auto' || overflowY === 'scroll') {
            scrollableElement = element
            break
          }
          element = element.parentElement
        }
      }
      
      // Fallback: try to find dialog with overflow-y-auto
      if (!scrollableElement) {
        scrollableElement = document.querySelector('[role="dialog"]')
      }
      
      // Scroll the container or fallback to window
      if (scrollableElement) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
    
    // Delay scroll slightly to ensure new content is rendered
    const timeoutId = setTimeout(scrollToTop, 50)
    return () => clearTimeout(timeoutId)
  }, [tattooId, tattoo])

  useEffect(() => {
    if (tattoo && artist) {
      trackEvent('item_viewed', {
        item_id: tattoo.id,
        item_type: tattoo.isFlash ? 'flash' : 'tattoo',
        artist_id: tattoo.artistId,
        source: 'tattoo_detail'
      })
    }
  }, [tattoo, artist, trackEvent])

  const handleShowArtist = () => {
    if (artist) {
      navigate(`/artist/${artist.id}`)
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
      return
    }

    const navigationState = location.state as DetailNavigationState | null
    const from = navigationState?.from

    if (from?.pathname) {
      const backPath = `${from.pathname}${from.search || ''}${from.hash || ''}`
      navigate(backPath, {
        replace: true,
        state: typeof navigationState?.scrollPosition === 'number'
          ? { scrollPosition: navigationState.scrollPosition }
          : undefined
      })
      return
    }

    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/profile')
  }

  const handleContactArtist = useCallback(async () => {
    if (!tattoo) return
    await trackEvent('artist_message_clicked', {
      artist_id: tattoo.artistId,
      source: 'tattoo_detail',
      item_id: tattoo.id,
      item_type: tattoo.isFlash ? 'flash' : 'tattoo'
    })
    navigate(`/chats/new?artistId=${tattoo.artistId}&source=tattoo_detail`)
  }, [tattoo, navigate, trackEvent])

  const handleContactArtistClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!user) {
      setAuthModalContext('contact')
      setShowAuthModal(true)
      return
    }

    handleContactArtist()
  }, [user, handleContactArtist])

  const handleAuthSuccess = () => {
    if (authModalContext === 'share') {
      setShowShareModal(true)
    } else {
      handleContactArtist()
    }
  }

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setShowAuthModal(open)
  }, [])

  const handleShare = () => {
    if (!user) {
      setAuthModalContext('share')
      setShowAuthModal(true)
      return
    }

    const currentUrl = shareUrl || window.location.href

    if (isMobileDevice() && navigator.share) {
      navigator.share({
        title: `Tattoo by ${artist?.displayName || 'Artist'}`,
        text: `Check out this tattoo!`,
        url: currentUrl,
      })
        .then(() => {
          const currentTattoo = tattoo
          if (!currentTattoo) return
          trackEvent('item_shared', {
            item_id: currentTattoo.id,
            item_type: currentTattoo.isFlash ? 'flash' : 'tattoo',
            artist_id: currentTattoo.artistId,
            source: 'tattoo_detail',
            share_method: 'native'
          })
        })
        .catch(console.error)
    } else {
      setShowShareModal(true)
    }
  }

  const handleAvatarClick = () => {
    handleShowArtist()
  }

  // Pinch-to-zoom and pan handlers
  const getTouchDistance = (touches: React.TouchList) => {
    const touch1 = touches[0]
    const touch2 = touches[1]
    const dx = touch2.clientX - touch1.clientX
    const dy = touch2.clientY - touch1.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  const getTouchCenter = (touches: React.TouchList) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY }
    }
    const touch1 = touches[0]
    const touch2 = touches[1]
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    }
  }

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartTimeRef.current = Date.now()
    
    if (e.touches.length === 2) {
      // Two finger pinch/zoom - prevent page scroll
      e.preventDefault()
      setIsZooming(true)
      setIsPanning(false)
      initialDistanceRef.current = getTouchDistance(e.touches)
      initialScaleRef.current = scale
      initialPositionRef.current = position
      lastTouchCenterRef.current = getTouchCenter(e.touches)
    } else if (e.touches.length === 1 && scale > 1) {
      // Single finger pan when zoomed - prevent page scroll
      e.preventDefault()
      setIsPanning(true)
      setIsZooming(false)
      initialPositionRef.current = position
      lastTouchCenterRef.current = getTouchCenter(e.touches)
    }
  }, [scale, position])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && isZooming) {
      e.preventDefault()
      
      // Calculate zoom
      const currentDistance = getTouchDistance(e.touches)
      const scaleFactor = currentDistance / initialDistanceRef.current
      const newScale = Math.max(1, Math.min(4, initialScaleRef.current * scaleFactor))
      
      // Calculate pan while zooming
      const currentCenter = getTouchCenter(e.touches)
      const deltaX = currentCenter.x - lastTouchCenterRef.current.x
      const deltaY = currentCenter.y - lastTouchCenterRef.current.y
      
      setScale(newScale)
      setPosition({
        x: initialPositionRef.current.x + deltaX,
        y: initialPositionRef.current.y + deltaY
      })
    } else if (e.touches.length === 1 && isPanning && scale > 1) {
      e.preventDefault()
      
      // Calculate pan
      const currentCenter = getTouchCenter(e.touches)
      const deltaX = currentCenter.x - lastTouchCenterRef.current.x
      const deltaY = currentCenter.y - lastTouchCenterRef.current.y
      
      setPosition({
        x: initialPositionRef.current.x + deltaX,
        y: initialPositionRef.current.y + deltaY
      })
    }
  }, [isZooming, isPanning, scale])

  const handleTouchEnd = useCallback(() => {
    const touchDuration = Date.now() - touchStartTimeRef.current
    
    if (isZooming || (isPanning && touchDuration < 100)) {
      // Reset zoom and position with smooth animation
      setScale(1)
      setPosition({ x: 0, y: 0 })
    }
    
    setIsZooming(false)
    setIsPanning(false)
  }, [isZooming, isPanning])

  if (loading) {
    return (
      <div className="bg-background md:pt-[52px]" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          {/* Back button skeleton */}
          <div className="flex items-center mb-6">
            <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
          </div>

          {/* Image skeleton with 1:1.4 aspect ratio */}
          <div className="mb-12 flex justify-center relative">
            <div className="relative max-w-screen-lg mx-auto rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)] w-full">
              <div 
                className="w-full bg-muted animate-pulse"
                style={{ aspectRatio: '1/1.4' }}
              />
            </div>

            {/* Contact button skeleton */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
              <div className="h-14 w-48 bg-muted rounded-lg animate-pulse" />
            </div>
          </div>

          {/* Info and Action Row skeleton */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              <div>
                <div className="h-5 w-32 bg-muted rounded animate-pulse" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !tattoo) {
    return (
      <div className="bg-background md:pt-[52px]">
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold">{t('common.error')}</h1>
          </div>
          <div className="text-center py-16">
            <p className="text-muted-foreground">{t('common.tattooNotFound')}</p>
            <Button onClick={handleBack} className="mt-4">
              {t('common.goBack')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div ref={contentRef} className="bg-background md:pt-[52px]" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </div>

          {/* Tattoo Image */}
          <div className="mb-12 flex justify-center relative">
            <div 
              ref={imageContainerRef}
              className="relative max-w-screen-lg mx-auto rounded-[var(--card-radius)] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.12)]"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                touchAction: 'none'
              }}
            >
              {!imageLoaded && (
                <div 
                  className="w-full bg-muted animate-pulse"
                  style={{ aspectRatio: tattoo.width && tattoo.height ? `${tattoo.width}/${tattoo.height}` : '1/1' }}
                />
              )}
              {/* Compressed image loads first for instant preview */}
              {tattoo.compressedImageUrl && (
                <img
                  src={tattoo.compressedImageUrl}
                  alt=""
                  aria-hidden="true"
                  className={`w-full h-auto object-cover lg:max-h-[60vh] lg:w-auto transition-opacity duration-300 ${
                    compressedImageLoaded && !fullImageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
                  }`}
                  onLoad={() => setCompressedImageLoaded(true)}
                />
              )}
              {/* Full-resolution image replaces compressed once loaded */}
              <img
                src={tattoo.imageUrl}
                alt={tattoo.caption || t('common.tattooDesign')}
                className={`w-full h-auto object-cover lg:max-h-[60vh] lg:w-auto transition-all duration-300 ${
                  fullImageLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'
                }`}
                style={{
                  transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                  transition: isZooming || isPanning ? 'none' : 'transform 0.3s ease-out',
                  transformOrigin: 'center center'
                }}
                onLoad={() => setFullImageLoaded(true)}
              />
            </div>

            {imageLoaded && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 animate-in fade-in-0 slide-in-from-bottom-4 duration-300">
                <div 
                  onClick={handleContactArtistClick}
                  className="text-flash-reservation font-medium px-6 py-4 rounded-lg transition-colors shadow-lg bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer flex items-center gap-2 whitespace-nowrap"
                >
                  <Send className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">Message Artist</span>
                </div>
              </div>
            )}
          </div>

          {/* Info and Action Row */}
          <div className="flex items-center justify-between mb-8">
            {imageLoaded ? (
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-75 transition-opacity animate-in fade-in-0 slide-in-from-left-4 duration-300"
                onClick={handleAvatarClick}
              >
                {artist?.avatarUrl && (
                  <img
                    src={artist.avatarUrl}
                    alt={artist.displayName}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                )}
                <div>
                  <div className="font-medium text-foreground">
                    {artist?.displayName || t('common.artistName')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
                <div>
                  <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                </div>
              </div>
            )}

            {imageLoaded ? (
              <div className="flex items-center gap-3 animate-in fade-in-0 slide-in-from-right-4 duration-300">
                <button 
                  type="button"
                  onClick={handleShare}
                  className="bg-secondary text-secondary-foreground w-12 h-12 rounded-lg flex items-center justify-center transition-colors hover:bg-secondary/80"
                >
                  <Share2 className="w-5 h-5" />
                </button>
                {tattoo && (
                  <SaveButton
                    item={tattoo}
                    onSaveStateChange={handleDetailSaveCountChange}
                    className={`bg-secondary text-secondary-foreground hover:bg-secondary/80 h-12 min-h-12 rounded-lg flex items-center justify-center shrink-0 transition-colors py-0 ${
                      detailPageSavesCount > 0
                        ? 'min-w-12 w-auto px-3 gap-1.5'
                        : 'w-12 px-0'
                    }`}
                    iconSize="w-5 h-5"
                    source="tattoo_detail"
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
                <div className="w-12 h-12 rounded-lg bg-muted animate-pulse" />
              </div>
            )}
          </div>

          {/* Similar Items Section */}
          <SimilarItems itemId={tattoo.id} itemType="portfolioItem" fallbackArtistId={tattoo.artistId} />
        </div>
      </div>
      
      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={handleAuthModalOpenChange}
        onSuccess={handleAuthSuccess}
        source="tattoo_detail_page"
        defaultMode="signup"
      />
      
      <ShareModal
        isOpen={showShareModal}
        onOpenChange={setShowShareModal}
        url={shareUrl || window.location.href}
        title={`Tattoo by ${artist?.displayName || 'Artist'}`}
        description="Check out this tattoo!"
        imageUrl={tattoo.imageUrl}
        itemId={tattoo.id}
        itemType={tattoo.isFlash ? 'flash' : 'tattoo'}
        artistId={tattoo.artistId}
        source="tattoo_detail"
      />

    </>
  )
}
